import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize, TextType} from '@Components/text';
import type {SelectableItemWithAudio} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {SelectableAiThumbnailItem} from '@Components/selectable-ai-thumbnail-item/selectable-ai-thumbnail-item';
import {noop} from '@Utils/general.util';
import {LoadingText} from '@Components/loading-text';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from './ai-subtitle-generating.module.scss';
import type {AiSubtitleGeneratingProps} from './ai-subtitle-generating.types';
import {AiSubtitleItemResult} from './ai-subtitle-generating.types';
import {useAppSelector} from '@/hooks';

const ROW_ITEM_LOADER = <CircularProgressLoader size={LOADER_SIZE.XSMALL} color={LOADER_COLOR.PRIMARY_DARK} classes={styles.loader} />;

export function AiSubtitleGenerating({isOpenedInContextMenu = false, isMobile = false, ...props}: AiSubtitleGeneratingProps): ReactElement {
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  const getProcessingItemsList = (): ReactElement | null => {
    if (props.items.length > 0) {
      return (
        <ul className={`${styles.listWrapper} spacing-m-0 spacing-p-l-4 spacing-p-r-4 flex-v-row`}>
          {props.items.map((item) => {
            return getItemRow(item);
          })}
        </ul>
      );
    }
    return null;
  };

  const getColorClassForItemStatus = (itemID: string): string => {
    if (props.results && props.results[itemID]) {
      switch (props.results[itemID]) {
        case AiSubtitleItemResult.FAILURE:
          return styles.itemFailure;
        case AiSubtitleItemResult.SUCCESS:
          return styles.itemSuccess;
        case AiSubtitleItemResult.RETRY:
          return styles.itemRetry;
        case AiSubtitleItemResult.LOADING:
          return '';
        default:
          throw new Error(`Unhandled status for subtitle item's color: ${props.results[itemID]}`);
      }
    }
    return '';
  };

  const getStatusIndicatorForItem = (itemId: string): ReactElement => {
    if (props.results && props.results[itemId]) {
      switch (props.results[itemId]) {
        case AiSubtitleItemResult.FAILURE:
          return <Icon className="content-danger" size={IconSize.SIZE_ICON_16} icon="icon-exclamation-triangle" />;
        case AiSubtitleItemResult.SUCCESS:
          return <Icon className="content-success" size={IconSize.SIZE_ICON_16} icon="icon-check" />;
        case AiSubtitleItemResult.RETRY:
          return (
            <Icon
              className={styles.retryIcon}
              shape={IconShape.CIRCLE}
              isUsedAsButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                void props.onRetry(itemId);
              }}
              size={IconSize.SIZE_ICON_16}
              icon="icon-refresh"
              type={IconType.SECONDARY}
            />
          );
        case AiSubtitleItemResult.LOADING:
          return ROW_ITEM_LOADER;
        default:
          throw new Error(`Unhandled status for subtitle item: ${props.results[itemId]}`);
      }
    }
    return ROW_ITEM_LOADER;
  };

  const getTextForGenerationResult = (itemId: string): string => {
    if (props.results && props.results[itemId]) {
      switch (props.results[itemId]) {
        case AiSubtitleItemResult.SUCCESS:
          return window.i18next.t('pmwjs_subtitles_generated_successfully');
        case AiSubtitleItemResult.FAILURE:
          return window.i18next.t('pmwjs_no_subtitles');
        case AiSubtitleItemResult.RETRY:
          return window.i18next.t('pmwjs_something_went_wrong');
        case AiSubtitleItemResult.LOADING:
          return '';
        default:
          throw new Error(`Unhandled status for generation result text: ${props.results[itemId]}`);
      }
    }

    return '';
  };

  const getClassNameForGenerationResult = (itemId: string): string => {
    if (props.results && props.results[itemId]) {
      switch (props.results[itemId]) {
        case AiSubtitleItemResult.SUCCESS:
          return 'content-success';
        case AiSubtitleItemResult.FAILURE:
        case AiSubtitleItemResult.RETRY:
          return 'content-danger';
        case AiSubtitleItemResult.LOADING:
          return '';
        default:
          throw new Error(`Unhandled status for generation result text's classname: ${props.results[itemId]}`);
      }
    }

    return '';
  };

  const getStatusText = (itemId: string): ReactElement => {
    if (props.results && props.results[itemId] && props.results[itemId] !== AiSubtitleItemResult.LOADING) {
      return <Text type={TextType.BODY} size={TextSize.XXSMALL} className={getClassNameForGenerationResult(itemId)} val={getTextForGenerationResult(itemId)} />;
    }
    return <LoadingText isAnimating textProps={{val: window.i18next.t('pmwjs_generating'), type: TextType.BODY, size: TextSize.XXSMALL, className: 'content-sub-text'}} />;
  };

  const getItemRow = (item: SelectableItemWithAudio): ReactElement => {
    return (
      <li key={item.id} className="flex-h-row flex-content-between flex-row-align-center spacing-p-0">
        <ul className={`flex-row-align-center ${styles.itemLeftCluster} spacing-m-0`}>
          <SelectableAiThumbnailItem isSmall id={`graphic-container-${item.id}`} isSelected={false} thumbnailImage={item.imageUrl} onClick={noop} />
          <li key={`text-container-${item.id}`} className="flex-v-row">
            <Text type={TextType.BODY} size={TextSize.XXSMALL} bold className={`body-content ${styles.itemName}`} val={item.name} />
            {getStatusText(item.id)}
          </li>
        </ul>

        <div className={`flex-center ${styles.generatingStatus} ${styles.loadingContainer} ${getColorClassForItemStatus(item.id)}`}>{getStatusIndicatorForItem(item.id)}</div>
      </li>
    );
  };

  const getInfoText = (): string => {
    if (aiSubtitleState !== AiSubtitleState.GENERATING) {
      return window.i18next.t('pmwjs_generated_x_of_y_subtitles', {
        success: Object.values(props.results).filter((val) => {
          return val === AiSubtitleItemResult.SUCCESS;
        }).length,
        total: props.items.length,
      });
    }

    if (props.items.length === 1) {
      return window.i18next.t('pmwjs_generating_subtitles');
    }

    return window.i18next.t('pmwjs_generating_subtitles_for_x_items', {x: props.items.length});
  };

  const getAiSubtitlesGeneratingPanelContent = (): ReactElement => {
    return (
      <div
        ref={props.scrollableContainerRef}
        className={`_full-width _full-height flex-v-row ${styles.listContainer} ${!isOpenedInContextMenu && !isMobile ? styles.listContainerMaxHeight : ''}`}
      >
        <div className={`flex-h-row _full-width spacing-p-b-3 ${styles.statusText}`}>
          <Text className={`content-body spacing-m-l-4 spacing-m-r-4 ${styles.text}`} type={TextType.BODY} size={TextSize.XXSMALL} val={getInfoText()} />
        </div>
        {getProcessingItemsList()}
      </div>
    );
  };

  return getAiSubtitlesGeneratingPanelContent();
}
